import { useState } from 'react'

import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import { GetPlayerListTableData } from '@/modules/manage-player/type'
import GetPlayerListTable from '../GetPlayerListTable'
import { PlayerFormModal } from '../PlayerFormModal'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Button } from '@/common/ui/button'
import { Download } from 'lucide-react'
import { useMutationExportPlayerListCsv } from '@/modules/manage-player/api/hooks/useMutationExportPlayerListCsv'
import { ExportCsvAmountModal } from '@/common/components/ExportCsvAmountModal'

import type { ExportPlayerListCsvRequest } from '@/modules/manage-player/type'

interface GetPlayerListListingProps {
	data: GetPlayerListTableData[]
	total: number
	isLoading?: boolean
	onSort?: (columnId: string, order: 'asc' | 'desc' | undefined) => void
	exportParams?: Omit<
		ExportPlayerListCsvRequest,
		'take' | 'skip' | 'filename' | 'columnMappings'
	>
}

export default function GetPlayerListListing({
	data,
	total,
	isLoading,
	onSort,
	exportParams,
}: GetPlayerListListingProps) {
	const { data: user } = useQueryProfile()
	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_PLAYER,
	)
	const exportCsvMutation = useMutationExportPlayerListCsv()
	const [showExportModal, setShowExportModal] = useState(false)

	const handleExportCsv = (amount: number) => {
		if (!exportParams) return

		exportCsvMutation.mutate({
			...exportParams,
			take: amount || total || 1000,
			skip: 0,
			filename: 'player_list',
			columnMappings: {
				phone: 'เบอร์โทรศัพท์',
				'mainWallet.balance': 'ยอดเงิน',
				'promotionWallet.balance': 'ยอดเงินติดเงื่อนไข',
				'rank.name': 'ระดับ',
				'channel.name': 'ช่องทาง',
				isBanned: 'สถานะแบน',
				createdAt: 'วันที่สมัคร',
				lastOnline: 'ใช้งานล่าสุด',
			},
		})
	}

	return (
		<div className="space-y-4">
			<div className="flex items-start justify-between">
				<Heading title={`จัดการสมาชิก`} description="จัดการสมาชิก" />
				<div className="flex gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setShowExportModal(true)}
						disabled={
							exportCsvMutation.isPending || !exportParams || !data.length
						}
						className="flex items-center gap-2"
					>
						<Download className="h-4 w-4" />
						โหลด CSV
					</Button>
					{hasWritePermission && <PlayerFormModal />}
				</div>
			</div>
			<Separator />
			<GetPlayerListTable
				data={data}
				total={total}
				isLoading={isLoading}
				onSort={onSort}
			/>
			<ExportCsvAmountModal
				open={showExportModal}
				onOpenChange={setShowExportModal}
				onConfirm={handleExportCsv}
				defaultAmount={total || 1000}
				maxAmount={100000}
				isLoading={exportCsvMutation.isPending}
			/>
		</div>
	)
}
